import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { IconButton, Tooltip } from '@material-ui/core'
import Icon from '../../../../components/atoms/Icon'
import { useAttachment } from '../contexts/AttachmentContext'

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    flexShrink: 0,
    minHeight: 36
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  layoutButton: {
    padding: 5,
    borderRadius: 4,
    border: '1px solid transparent',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  layoutButtonActive: {
    backgroundColor: 'var(--color-primary-100, rgba(25, 118, 210, 0.08))',
    border: '1px solid var(--color-primary-300, rgba(25, 118, 210, 0.3))'
  },
  divider: {
    width: 1,
    height: 20,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 0.5)
  }
}))

function LayoutIcon ({ leftWeight, rightWeight, active }) {
  const total = leftWeight + rightWeight
  const leftW = Math.round((leftWeight / total) * 16)
  const rightW = 16 - leftW
  const gap = 1.5
  const fill = active ? 'var(--color-primary-500, #1976d2)' : 'currentColor'
  const opacity = active ? 1 : 0.5

  if (leftWeight === 0) {
    return (
      <svg width='18' height='14' viewBox='0 0 18 14' style={{ display: 'block' }}>
        <rect x='1' y='1' width='16' height='12' rx='1.5' fill={fill} opacity={opacity} />
      </svg>
    )
  }

  return (
    <svg width='18' height='14' viewBox='0 0 18 14' style={{ display: 'block' }}>
      <rect x='1' y='1' width={leftW - gap / 2} height='12' rx='1.5' fill={fill} opacity={opacity * 0.35} />
      <rect x={1 + leftW + gap / 2} y='1' width={rightW - gap / 2} height='12' rx='1.5' fill={fill} opacity={opacity} />
    </svg>
  )
}

LayoutIcon.propTypes = {
  leftWeight: PropTypes.number.isRequired,
  rightWeight: PropTypes.number.isRequired,
  active: PropTypes.bool
}

const PRESETS = [
  { mode: 'sidebar', left: 3, right: 1, label: 'Sidebar' },
  { mode: 'split', left: 1, right: 1, label: 'Split' },
  { mode: 'focus', left: 1, right: 3, label: 'Focus' },
  { mode: 'full', left: 0, right: 1, label: 'Full width' }
]

export default function PanelToolbar ({ viewerActions, onPopout }) {
  const classes = useStyles()
  const { layoutMode, setLayoutMode } = useAttachment()

  const handlePopout = useCallback(() => {
    if (onPopout) onPopout()
  }, [onPopout])

  return (
    <div className={classes.toolbar}>
      <div className={classes.leftGroup}>
        {PRESETS.map(({ mode, left, right, label }) => (
          <Tooltip key={mode} title={label} placement='bottom' enterDelay={400}>
            <IconButton
              className={`${classes.layoutButton} ${layoutMode === mode ? classes.layoutButtonActive : ''}`}
              onClick={() => setLayoutMode(mode)}
              size='small'
              aria-label={label}
            >
              <LayoutIcon leftWeight={left} rightWeight={right} active={layoutMode === mode} />
            </IconButton>
          </Tooltip>
        ))}

        <div className={classes.divider} />

        <Tooltip title={onPopout ? 'Open in new window' : 'Not available for inline attachments'} placement='bottom' enterDelay={400}>
          <span>
            <IconButton
              className={classes.layoutButton}
              onClick={handlePopout}
              size='small'
              aria-label='Open in new window'
              disabled={!onPopout}
            >
              <Icon name='openInNew' customSize='16px' />
            </IconButton>
          </span>
        </Tooltip>

      </div>

      {viewerActions && (
        <div className={classes.rightGroup}>
          {viewerActions}
        </div>
      )}
    </div>
  )
}

PanelToolbar.propTypes = {
  viewerActions: PropTypes.node,
  onPopout: PropTypes.func
}
